import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link as RouterLink, useHistory } from "react-router-dom";
import moment from "moment";
import { ROUTES } from "routes/constants";
import {
  Box,
  Chip,
  Grid,
  Typography,
  Stack,
  List,
  ListItem,
  Divider,
  Tooltip
} from "@mui/material";
import _ from "lodash";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ClientContactModal from "./ClientContactModal";
import Header from "components/Header";
import {
  showNote,
  ltvConverter,
  moneyFieldConverterTBCFormatter,
  moneyFieldConverterTBC
} from "utils";
import { getCase, getCaseProducts, getCaseTask, updateCase } from "reducers/cases";
import CaseCounterProgress from "./CaseCounterProgress";
import CaseDetailTabs from "./CaseDetailTabs";
import { makeStyles } from "@mui/styles";
import { WORDS_110 } from "utils/constants";
import BackBtn from "./BackBtn";
import CaseInformativePopup from "./CaseInformativePopup";
import { updateFilters } from "reducers/leads";

const CHIP_STYLE = theme => {
  return {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.active.selected,
    fontSize: "13px",
    fontWeight: "700",
    fontFamily: "'Nunito Sans', sans-serif",
    marginRight: "24px",
    marginBottom: "8px",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      height: "24px",
      fontSize: "11px"
    }
  };
};

const useStyles = makeStyles(theme => ({
  box: {
    padding: "24px",
    borderRadius: "16px",
    backgroundColor: theme.palette.text.dark.primary,
    border: `1px solid ${theme.palette.text.dark.primary}`,
    boxShadow: `3px 5px 5px -1px ${theme.palette.boxShadowPalette.primary}`,
    [theme.breakpoints.down("sm")]: {
      padding: "20px 14px"
    }
  }
}));

function Case() {
  const classes = useStyles();
  const {
    location: { state }
  } = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [isModal, setIsModal] = useState(false);
  const [caseStatusDetail, setCaseStatusDetail] = useState([]);
  const [propertyType, setPropertyType] = useState("");
  const [clientDetail, setClientDetail] = useState({});
  const {
    data,
    filters,
    page,
    caseDetailActiveTab,
    caseStatuses,
    caseProducts
  } = useSelector(state => state.cases);

  useEffect(() => {
    dispatch(getCase(id));
  }, [dispatch, filters, page, caseDetailActiveTab]);

  useEffect(() => {
    if (caseStatuses?.results?.length) {
      setCaseStatusDetail(caseStatuses.results);
    } else {
      setCaseStatusDetail([]);
    }
  }, [caseStatuses]);

  useEffect(() => {
    dispatch(getCaseProducts());
    dispatch(getCaseTask());
  }, [dispatch]);

  const location = {
    pathname: ROUTES.CASES,
    state: { page: state?.page }
  };

  return (
    <>
      <Header
        componentName={`Case #${id}`}
        isInfoIcon={true}
        backBtn={true}
        routePage={location}
        caseData={data}
      />
      <div style={{ marginTop: "24px" }}>
        <Stack display={{ xs: "block", lg: "none" }} marginBottom="24px">
          <Stack direction="row" alignItems="center">
            <BackBtn routePage={ROUTES.CASES} />
            <Typography variant="h3" color="text.primary" fontSize="38px">
              {`Case #${id}`}
            </Typography>
            {<CaseInformativePopup caseData={data} />}
          </Stack>
        </Stack>
        <Box
          sx={{
            display: "flex",
            marginBottom: "32px",
            flexWrap: "wrap"
          }}
        >
          {data?.paidPercentage && (
            <>
              <Box
                className={classes.box}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flex: 1
                }}
              >
                <Stack direction="row" flexWrap="wrap">
                  <Stack
                    sx={theme => ({
                      marginRight: "56px",
                      minWidth: "170px",
                      [theme.breakpoints.down("xxl")]: {
                        marginRight: "16px"
                      },
                      [theme.breakpoints.down("lg")]: {
                        marginRight: "16px"
                      }
                    })}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      component="h2"
                    >
                      Paid
                    </Typography>
                    <Typography
                      variant="h4"
                      color="text.primary"
                      component="h4"
                      marginBottom="24px"
                      wordBreak="break-word"
                    >
                      £{data.grossPaid}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      component="h2"
                    >
                      Pending
                    </Typography>
                    <Typography
                      variant="h4"
                      color="text.primary"
                      component="h4"
                      marginBottom="12px"
                      wordBreak="break-word"
                    >
                      £{data.grossPending}
                    </Typography>
                  </Stack>
                  <Stack sx={{ minWidth: "170px" }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      component="h2"
                    >
                      Commission
                    </Typography>
                    <Typography
                      variant="h4"
                      color="text.primary"
                      component="h4"
                      marginBottom="24px"
                      wordBreak="break-word"
                    >
                      £{data.commission}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      component="h2"
                    >
                      Pending Commission
                    </Typography>
                    <Typography
                      variant="h4"
                      color="text.primary"
                      component="h4"
                      marginBottom="12px"
                      wordBreak="break-word"
                    >
                      £{data.pendingCommission}
                    </Typography>
                  </Stack>
                </Stack>
                {data.paidPercentage && (
                  <CaseCounterProgress
                    progressPercentage={data.paidPercentage}
                  />
                )}
              </Box>
              <Box
                className={classes.box}
                sx={theme => ({
                  flex: "0 0 400px",
                  marginLeft: "14px",
                  [theme.breakpoints.down("xxl")]: {
                    flex: "0 0 300px"
                  },
                  [theme.breakpoints.down("xll")]: {
                    flex: "0 0 300px"
                  },
                  [theme.breakpoints.down("xl")]: {
                    flex: 1
                  },
                  [theme.breakpoints.down("llg")]: {
                    marginTop: "24px",
                    marginLeft: 0,
                    flex: "unset",
                    width: "100%"
                  },
                  [theme.breakpoints.down("lg")]: {
                    marginTop: 0,
                    marginLeft: "16px",
                    flex: "0 0 280px"
                  },
                  [theme.breakpoints.down("md")]: {
                    marginTop: "24px",
                    marginLeft: 0,
                    flex: "unset",
                    width: "100%"
                  }
                })}
              >
                <Box>
                  <Typography variant="body1" color="text.primary">
                    ID: {data.lead.id}
                  </Typography>
                  <List sx={{ display: "flex" }}>
                    <ListItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        padding: 0
                      }}
                    >
                      <Typography
                        gutterBottom
                        variant="body1"
                        color="text.secondary"
                        component="p"
                      >
                        Property
                      </Typography>
                      <Tooltip
                        title={moneyFieldConverterTBC(data.lead.propertyAmount)}
                        placement="top"
                      >
                        <Typography
                          gutterBottom
                          variant="h6"
                          color="text.primary"
                          component="h6"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {moneyFieldConverterTBCFormatter(
                            data.lead.propertyAmount,
                            data.lead.formattedPropertyAmount
                          )}
                        </Typography>
                      </Tooltip>
                    </ListItem>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ margin: "0 12px" }}
                    />
                    <ListItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        padding: "0 0 0 24px"
                      }}
                    >
                      <Typography
                        gutterBottom
                        variant="body1"
                        color="text.secondary"
                        component="p"
                      >
                        Mortgage
                      </Typography>
                      <Tooltip
                        title={moneyFieldConverterTBC(data.lead.mortgageAmount)}
                        placement="top"
                      >
                        <Typography
                          gutterBottom
                          variant="h6"
                          color="text.primary"
                          component="h6"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {moneyFieldConverterTBCFormatter(
                            data.lead.mortgageAmount,
                            data.lead.formattedMortgageAmount
                          )}
                        </Typography>
                      </Tooltip>
                    </ListItem>
                  </List>
                  <List style={{ display: "flex" }}>
                    <ListItem style={{ alignItems: "center", padding: 0 }}>
                      <CalendarTodayIcon
                        sx={{ color: "text.secondary" }}
                        style={{ marginRight: "24px" }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {moment(data.lead.modified).format(
                          "ddd D MMM YYYY [at] hh:mm A"
                        )}
                      </Typography>
                    </ListItem>
                  </List>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <Stack sx={{ color: "text.secondary" }}>
                      <svg
                        width="25"
                        height="25"
                        viewBox="0 0 25 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.46 23.92C18.7892 23.92 23.92 18.7892 23.92 12.46C23.92 6.13082 18.7892 1 12.46 1C6.13082 1 1 6.13082 1 12.46C1 18.7892 6.13082 23.92 12.46 23.92Z"
                          stroke="currentcolor"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M5.20996 9.95996H6.23996V14.75H8.86996V15.6H5.20996V9.95996Z"
                          fill="currentcolor"
                          stroke="currentcolor"
                          strokeWidth="0.5"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M10.4401 10.8H8.6001V9.95996H13.3101V10.81H11.4701V15.6H10.4401V10.8Z"
                          fill="currentcolor"
                          stroke="currentcolor"
                          strokeWidth="0.5"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M20.2802 9.95996L17.7702 15.6H16.9302L14.4102 9.95996H15.4702L17.3402 14.32L19.2202 9.95996H20.2802Z"
                          fill="currentcolor"
                          stroke="currentcolor"
                          strokeWidth="0.5"
                          strokeMiterlimit="10"
                        />
                      </svg>
                    </Stack>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      marginLeft="24px"
                      sx={{
                        "&:hover": {
                          cursor: "pointer"
                        }
                      }}
                    >
                      {ltvConverter(data.lead.ltv)}
                    </Typography>
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0 }}>
                    <AccountCircleIcon sx={{ color: "text.secondary" }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: data.lead.client.isAdverseCredit
                          ? "error.main"
                          : "primary.light",
                        marginLeft: "24px",
                        "&:hover": {
                          cursor: "pointer"
                        }
                      }}
                      onClick={e => {
                        setIsModal(true);
                        setClientDetail(data);
                      }}
                    >
                      {_.isEmpty(data.lead.client.fullName)
                        ? "N/A"
                        : data.lead.client.fullName}
                    </Typography>
                  </ListItem>
                  <Stack sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                    <ListItem style={{ paddingLeft: 0, minWidth: "50%", width: "fit-content" }}>
                      {data?.lead?.propertyType === "BUY_TO_LET" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id="mask0_35_171" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="2" y="3" width="21" height="19">
                              <path d="M22.5 3.5H2.5V21.5H22.5V3.5Z" fill="white" />
                            </mask>
                            <g mask="url(#mask0_35_171)">
                              <path d="M22.4995 4.94824C22.4063 5.10969 22.2919 5.4806 22.0463 5.69005C19.9497 7.45296 17.8362 9.19404 15.7142 10.9264C15.2483 11.3061 14.7104 11.2712 14.1894 10.8697C12.94 9.91842 11.6905 8.97151 10.4664 7.98533C10.1107 7.69733 9.89465 7.74969 9.56854 8.02023C7.94633 9.3773 6.31143 10.7082 4.67229 12.0435C4.00308 12.5889 3.36352 12.6021 2.86797 12.0959C2.30888 11.5242 2.38936 10.6995 3.07974 10.1279C4.96877 8.56569 6.86204 7.00787 8.75532 5.45006C8.84849 5.37587 8.93743 5.29733 9.02638 5.22315C9.70404 4.65151 10.2208 4.64278 10.9197 5.18824C12.131 6.13514 13.3508 7.07333 14.5452 8.03769C14.8544 8.28642 15.0535 8.29078 15.3584 8.03333C17.0145 6.63696 18.6875 5.26678 20.3563 3.89224C20.7714 3.54751 21.2204 3.35987 21.7455 3.61296C22.2199 3.83987 22.4571 4.22387 22.4995 4.94824Z" fill="currentcolor" />
                              <path d="M8.79741 16.3341C8.79741 15.0599 8.79319 13.7857 8.79741 12.5115C8.80163 11.726 9.22519 11.2766 9.95796 11.2635C10.6822 11.2504 11.1777 11.6999 11.182 12.4591C11.199 15.0729 11.199 17.6911 11.182 20.3049C11.1777 21.086 10.678 21.5137 9.91135 21.4918C9.20824 21.47 8.80591 21.0293 8.80163 20.2569C8.79319 18.9522 8.79741 17.6431 8.79741 16.3341Z" fill="currentcolor" />
                              <path d="M18.8322 16.3375C18.8322 15.0633 18.828 13.7891 18.8322 12.5149C18.8364 11.7294 19.26 11.28 19.9928 11.2669C20.717 11.2538 21.2125 11.7033 21.2168 12.4625C21.2338 15.0763 21.2338 17.6945 21.2168 20.3083C21.2125 21.0894 20.7128 21.5171 19.9461 21.4953C19.243 21.4734 18.8364 21.0327 18.8364 20.2604C18.828 18.9513 18.8322 17.6422 18.8322 16.3375Z" fill="currentcolor" />
                              <path d="M16.1756 17.6736C16.1756 18.5508 16.1799 19.4278 16.1756 20.3093C16.1714 21.0031 15.7944 21.4438 15.1803 21.4962C14.4306 21.5617 13.8334 21.1776 13.8164 20.4707C13.7699 18.6118 13.7699 16.7529 13.808 14.894C13.8207 14.2133 14.4052 13.8119 15.121 13.8598C15.7817 13.9078 16.1756 14.3398 16.1799 15.038C16.1799 15.9195 16.1756 16.7966 16.1756 17.6736Z" fill="currentcolor" />
                              <path d="M6.14666 18.3406C6.14666 18.9952 6.15089 19.6497 6.14666 20.3042C6.14242 20.998 5.7697 21.4388 5.15132 21.4955C4.40163 21.561 3.80867 21.1813 3.78325 20.4744C3.73243 19.0475 3.73666 17.6162 3.78325 16.1937C3.80443 15.5217 4.38469 15.094 5.03696 15.1289C5.7104 15.1638 6.13395 15.622 6.14242 16.3289C6.15089 16.9966 6.14666 17.6686 6.14666 18.3406Z" fill="currentcolor" />
                            </g>
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.propertyType === "COMMERCIAL" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.0716 10.281C18.9722 10.281 19.8035 10.281 20.6348 10.281C21.8552 10.281 22.5 10.9381 22.5 12.1768C22.5 14.6544 22.5 17.1373 22.5 19.6149C22.5 20.8698 21.8765 21.4946 20.6348 21.4946C18.4499 21.4946 16.265 21.4892 14.0801 21.5C13.7497 21.5 13.6378 21.4138 13.6484 21.0691C13.6697 20.1104 13.6591 19.1463 13.6538 18.1822C13.6484 17.4336 13.2114 16.9973 12.4973 17.0027C11.8099 17.0081 11.3889 17.4443 11.3889 18.1768C11.3836 19.2594 11.3889 20.3366 11.3889 21.4838C11.2024 21.4838 11.0478 21.4838 10.888 21.4838C8.70306 21.4838 6.51815 21.4838 4.33323 21.4838C3.18216 21.4838 2.51069 20.7998 2.51069 19.6311C2.51069 16.0117 2.52135 12.3869 2.50004 8.7675C2.49471 7.85727 3.09689 6.83932 4.32791 6.92011C5.05266 6.96858 5.78274 6.91472 6.51282 6.93627C6.85388 6.94704 6.99243 6.85548 6.97645 6.48384C6.9498 6.00449 6.94447 5.51975 7.00842 5.04578C7.13632 4.08169 7.73317 3.52154 8.68708 3.51616C11.245 3.49461 13.803 3.49461 16.3556 3.51616C17.4107 3.52693 18.0715 4.25404 18.0822 5.3474C18.0929 6.807 18.0875 8.26122 18.0875 9.72083C18.0715 9.88779 18.0716 10.0494 18.0716 10.281ZM6.95513 10.3079C6.95513 10.1732 6.95513 10.0386 6.95513 9.90395C6.9498 9.27917 6.77927 9.10144 6.1611 9.09066C5.86267 9.08528 5.56424 9.07451 5.27115 9.10144C4.7862 9.14991 4.64765 9.38151 4.64765 10.0978C4.64232 11.1373 4.74357 11.3689 5.26582 11.4174C5.63352 11.4497 6.00656 11.439 6.37426 11.4174C6.74197 11.3959 6.93381 11.175 6.9498 10.8034C6.96046 10.6418 6.95513 10.4749 6.95513 10.3079ZM15.8813 10.281C15.8813 10.1786 15.8813 10.0709 15.8813 9.96858C15.876 9.26302 15.7161 9.09605 15.018 9.09066C14.7675 9.09066 14.5117 9.08528 14.2613 9.09605C13.8349 9.11221 13.6005 9.33842 13.5845 9.7693C13.5685 10.0978 13.5685 10.4318 13.5845 10.7603C13.6005 11.1912 13.8403 11.4174 14.2613 11.4336C14.5863 11.4443 14.9167 11.4497 15.2418 11.4282C15.6948 11.4013 15.8653 11.2074 15.8813 10.7388C15.8866 10.5826 15.8813 10.4318 15.8813 10.281ZM6.95513 17.0404C6.95513 15.9147 6.89118 15.8501 5.77208 15.8501C5.62287 15.8501 5.47365 15.8447 5.32444 15.8555C4.92476 15.877 4.63699 16.0925 4.66896 16.4964C4.7116 17.0673 4.35455 17.7621 5.08463 18.1553C5.16989 18.2038 5.28713 18.1984 5.38839 18.1984C5.67083 18.2038 5.95327 18.2092 6.23571 18.1984C6.75796 18.1822 6.94447 17.9829 6.95513 17.4443C6.96046 17.3097 6.95513 17.175 6.95513 17.0404ZM20.3417 17.0889C20.3471 15.9039 20.2938 15.8501 19.1214 15.8501C18.9562 15.8501 18.7963 15.8447 18.6311 15.8662C18.2741 15.9147 18.0662 16.0978 18.0716 16.491C18.0769 16.868 18.0716 17.2397 18.0769 17.6167C18.0822 17.956 18.2847 18.1553 18.5991 18.1822C18.9988 18.2145 19.3985 18.2145 19.7982 18.1822C20.1392 18.1553 20.3311 17.9399 20.3417 17.5844C20.3471 17.4228 20.3417 17.2558 20.3417 17.0889ZM11.4209 10.2433C11.4209 10.1086 11.4209 9.97397 11.4209 9.83932C11.4102 9.32765 11.2344 9.12298 10.7388 9.09605C10.3977 9.07989 10.0566 9.07989 9.71558 9.10144C9.35854 9.12837 9.14538 9.33303 9.1507 9.71544C9.1507 10.0601 9.14005 10.4048 9.1507 10.7496C9.16136 11.2127 9.33722 11.3905 9.79552 11.4282C9.94473 11.439 10.0939 11.4336 10.2378 11.4336C11.3569 11.4336 11.4209 11.3689 11.4209 10.2433ZM14.7462 5.73519C13.6271 5.73519 13.5738 5.78366 13.5792 6.93088C13.5792 7.10862 13.5738 7.29174 13.5951 7.46948C13.6378 7.81418 13.8403 8.01885 14.1813 8.03501C14.549 8.05117 14.9221 8.05655 15.2951 8.03501C15.6841 8.01346 15.8813 7.78725 15.8813 7.38869C15.8813 7.08707 15.8866 6.79084 15.8813 6.48923C15.876 5.89677 15.7268 5.74596 15.1459 5.73519C15.0127 5.73519 14.8794 5.73519 14.7462 5.73519ZM5.7934 14.7837C5.94261 14.7837 6.09182 14.7837 6.24104 14.7837C6.80059 14.7729 6.95513 14.6221 6.96046 14.0619C6.96579 13.7496 6.96046 13.4318 6.96046 13.1194C6.95513 12.7154 6.74197 12.5054 6.35828 12.4838C6.01722 12.4677 5.67616 12.4677 5.3351 12.4785C4.89811 12.4946 4.68495 12.7047 4.66364 13.1517C4.64765 13.4641 4.64765 13.7819 4.66364 14.0943C4.69028 14.6005 4.88213 14.7729 5.39904 14.7837C5.52161 14.789 5.66017 14.7837 5.7934 14.7837ZM14.7302 14.7837C14.8635 14.7837 14.9967 14.7837 15.1299 14.7837C15.7268 14.7783 15.8706 14.6382 15.8813 14.0458C15.8866 13.8034 15.8813 13.5664 15.8813 13.3241C15.876 12.6346 15.7214 12.4785 15.0393 12.4731C13.5845 12.4569 13.5365 12.5054 13.5738 13.9758C13.5898 14.6059 13.7603 14.7783 14.3732 14.7837C14.4904 14.789 14.613 14.7837 14.7302 14.7837ZM9.1507 6.85009C9.1507 7.04399 9.1507 7.23788 9.1507 7.43716C9.15603 7.79802 9.34255 8.00808 9.68894 8.02962C10.0886 8.05655 10.4883 8.05655 10.888 8.02962C11.229 8.00269 11.4209 7.78725 11.4209 7.43178C11.4209 7.26481 11.4209 7.10323 11.4209 6.93627C11.4209 5.74057 11.4209 5.74057 10.2325 5.74057C9.1507 5.73519 9.1507 5.73519 9.1507 6.85009ZM11.4209 13.6095C11.4209 13.491 11.4209 13.3671 11.4209 13.2487C11.4156 12.6562 11.245 12.4838 10.6428 12.4731C10.5416 12.4731 10.435 12.4731 10.3338 12.4731C9.17202 12.4731 9.1507 12.4946 9.1507 13.6472C9.1507 14.7837 9.1507 14.7837 10.2591 14.7837C11.4209 14.7837 11.4209 14.7837 11.4209 13.6095ZM20.3471 13.6526C20.3471 13.5341 20.3471 13.4102 20.3471 13.2917C20.3417 12.6562 20.1819 12.4838 19.5637 12.4731C19.2653 12.4677 18.9722 12.4623 18.6737 12.4785C18.29 12.5 18.0769 12.7047 18.0716 13.1086C18.0716 13.2756 18.0716 13.4372 18.0716 13.6041C18.0716 14.7837 18.0715 14.7837 19.228 14.7837C20.3311 14.7837 20.3471 14.7675 20.3471 13.6526Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.propertyType === "RESIDENTIAL" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.4696 21.4971C10.6726 21.4971 8.87569 21.4971 7.07873 21.4971C5.67581 21.4971 4.7362 20.5502 4.72754 19.1211C4.71888 17.4201 4.71887 15.7235 4.73186 14.0226C4.73619 13.6982 4.65392 13.5404 4.31185 13.5798C4.10834 13.6017 3.89617 13.5842 3.68833 13.5842C3.13842 13.5798 2.75738 13.3475 2.58851 12.9135C2.40232 12.4444 2.50624 12.0016 2.93491 11.6158C3.8572 10.7873 4.78815 9.97187 5.71911 9.15209C7.67627 7.42483 9.63777 5.69758 11.5906 3.96594C12.2747 3.36096 12.7814 3.33466 13.4698 3.9484C16.0548 6.24995 18.6355 8.56026 21.2162 10.8706C21.528 11.1511 21.8441 11.4186 22.1472 11.7079C22.4936 12.0367 22.5975 12.4795 22.4026 12.8959C22.2078 13.3168 21.8917 13.593 21.3981 13.5842C21.1773 13.5798 20.9521 13.6061 20.7313 13.5798C20.3719 13.5404 20.2766 13.685 20.2809 14.0357C20.2983 15.7498 20.2939 17.464 20.2939 19.1781C20.2939 20.5371 19.3803 21.4884 18.038 21.4928C16.1848 21.5059 14.3272 21.4971 12.4696 21.4971ZM12.5086 11.629C12.4263 11.5676 12.3787 11.5325 12.3354 11.4931C11.3438 10.5725 10.0491 10.5505 9.19178 11.4449C8.33877 12.3348 8.37773 13.685 9.30436 14.6363C10.2007 15.557 11.1143 16.46 12.0279 17.3631C12.383 17.7138 12.6298 17.7138 12.9849 17.3631C13.9375 16.4162 14.8901 15.4693 15.8254 14.5004C16.4705 13.8297 16.5874 13.0187 16.267 12.1638C15.9509 11.3133 15.1455 10.796 14.2536 10.7917C13.5521 10.7785 13.0195 11.138 12.5086 11.629Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.propertyType === null &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.689 3.5C15.4991 3.56337 18.1922 4.43158 20.3553 6.68131C23.3195 9.7739 23.1962 14.3874 20.0779 17.3279C18.2846 19.02 16.1462 19.8692 13.7489 20.148C12.0357 20.3445 10.3842 20.1227 8.74492 19.6284C8.56004 19.5713 8.28272 19.622 8.1225 19.7298C6.78521 20.617 5.36165 21.257 3.7717 21.4662C3.70391 21.4725 3.63612 21.4915 3.5745 21.4852C3.22323 21.4598 2.81034 21.6373 2.58232 21.2C2.34198 20.7374 2.68092 20.4585 2.92126 20.1607C3.61764 19.2988 4.10448 18.3229 4.29552 17.2202C4.33866 16.9857 4.27087 16.6625 4.13529 16.4787C1.55933 13.101 2.07699 8.69023 5.36781 5.97154C7.45077 4.25414 9.86035 3.53169 12.689 3.5ZM12.3131 6.67497C12.3131 6.68131 12.3131 6.68131 12.3131 6.68765C11.8447 6.68765 11.3763 6.6623 10.908 6.69399C10.0329 6.75102 9.33036 7.35306 9.09619 8.22127C8.93596 8.82331 9.127 9.2796 9.63233 9.49506C10.1007 9.69152 10.5506 9.49506 10.8772 9.01977C10.9881 8.86133 11.2223 8.70924 11.4072 8.7029C12.0604 8.66488 12.7136 8.65854 13.3607 8.7029C13.5764 8.72191 13.9277 8.91837 13.9462 9.0768C13.977 9.29227 13.8167 9.62815 13.638 9.7739C13.2621 10.0781 12.8307 10.3379 12.3932 10.5344C11.5982 10.8893 11.4441 11.523 11.5427 12.3025C11.5982 12.7397 11.8324 13.0376 12.2514 13.1327C12.7013 13.2341 13.145 13.1327 13.3114 12.6764C13.4778 12.2201 13.7921 12.011 14.1618 11.8082C14.4145 11.6687 14.661 11.523 14.8952 11.3582C15.8319 10.6928 16.1893 9.59646 15.8627 8.44941C15.5792 7.4608 14.6302 6.72567 13.5641 6.68765C13.1512 6.6623 12.7321 6.67497 12.3131 6.67497ZM11.2346 15.7119C11.2346 16.5168 11.7399 17.0618 12.4918 17.0681C13.2313 17.0744 13.7736 16.5041 13.7736 15.7246C13.7736 14.9324 13.2991 14.4318 12.5349 14.4191C11.7153 14.4128 11.2346 14.8881 11.2346 15.7119Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.propertyType === "TBC" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.689 3.5C15.4991 3.56337 18.1922 4.43158 20.3553 6.68131C23.3195 9.7739 23.1962 14.3874 20.0779 17.3279C18.2846 19.02 16.1462 19.8692 13.7489 20.148C12.0357 20.3445 10.3842 20.1227 8.74492 19.6284C8.56004 19.5713 8.28272 19.622 8.1225 19.7298C6.78521 20.617 5.36165 21.257 3.7717 21.4662C3.70391 21.4725 3.63612 21.4915 3.5745 21.4852C3.22323 21.4598 2.81034 21.6373 2.58232 21.2C2.34198 20.7374 2.68092 20.4585 2.92126 20.1607C3.61764 19.2988 4.10448 18.3229 4.29552 17.2202C4.33866 16.9857 4.27087 16.6625 4.13529 16.4787C1.55933 13.101 2.07699 8.69023 5.36781 5.97154C7.45077 4.25414 9.86035 3.53169 12.689 3.5ZM12.3131 6.67497C12.3131 6.68131 12.3131 6.68131 12.3131 6.68765C11.8447 6.68765 11.3763 6.6623 10.908 6.69399C10.0329 6.75102 9.33036 7.35306 9.09619 8.22127C8.93596 8.82331 9.127 9.2796 9.63233 9.49506C10.1007 9.69152 10.5506 9.49506 10.8772 9.01977C10.9881 8.86133 11.2223 8.70924 11.4072 8.7029C12.0604 8.66488 12.7136 8.65854 13.3607 8.7029C13.5764 8.72191 13.9277 8.91837 13.9462 9.0768C13.977 9.29227 13.8167 9.62815 13.638 9.7739C13.2621 10.0781 12.8307 10.3379 12.3932 10.5344C11.5982 10.8893 11.4441 11.523 11.5427 12.3025C11.5982 12.7397 11.8324 13.0376 12.2514 13.1327C12.7013 13.2341 13.145 13.1327 13.3114 12.6764C13.4778 12.2201 13.7921 12.011 14.1618 11.8082C14.4145 11.6687 14.661 11.523 14.8952 11.3582C15.8319 10.6928 16.1893 9.59646 15.8627 8.44941C15.5792 7.4608 14.6302 6.72567 13.5641 6.68765C13.1512 6.6623 12.7321 6.67497 12.3131 6.67497ZM11.2346 15.7119C11.2346 16.5168 11.7399 17.0618 12.4918 17.0681C13.2313 17.0744 13.7736 16.5041 13.7736 15.7246C13.7736 14.9324 13.2991 14.4318 12.5349 14.4191C11.7153 14.4128 11.2346 14.8881 11.2346 15.7119Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        marginLeft="10px"
                      >
                        {data?.lead?.propertyTypeDisplay || "TBC"}
                      </Typography>
                    </ListItem>
                    <ListItem style={{ paddingLeft: 0, minWidth: "50%", width: "fit-content" }}>
                      {
                        data?.lead?.financing === "PURCHASE" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.5 4.34375C2.5 3.87617 2.87471 3.5 3.34046 3.5H4.93383C5.70426 3.5 6.38713 3.95 6.7058 4.625H21.0987C22.0197 4.625 22.6921 5.50391 22.4504 6.39688L21.0146 11.7512C20.717 12.8551 19.7189 13.625 18.5808 13.625H8.47777L8.66688 14.627C8.74392 15.0242 9.09061 15.3125 9.49333 15.3125H19.5894C20.0551 15.3125 20.4298 15.6887 20.4298 16.1563C20.4298 16.6238 20.0551 17 19.5894 17H9.49333C8.28167 17 7.2416 16.1352 7.01748 14.9434L5.21049 5.41601C5.18597 5.28242 5.07041 5.1875 4.93383 5.1875H3.34046C2.87471 5.1875 2.5 4.81133 2.5 4.34375ZM6.98246 19.8125C6.98246 18.8809 7.73537 18.125 8.66338 18.125C9.59139 18.125 10.3443 18.8809 10.3443 19.8125C10.3443 20.7441 9.59139 21.5 8.66338 21.5C7.73537 21.5 6.98246 20.7441 6.98246 19.8125ZM18.7489 18.125C19.6769 18.125 20.4298 18.8809 20.4298 19.8125C20.4298 20.7441 19.6769 21.5 18.7489 21.5C17.8209 21.5 17.068 20.7441 17.068 19.8125C17.068 18.8809 17.8209 18.125 18.7489 18.125Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.financing === "REMORTGAGE" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.7734 8.4302H15.6038C14.8402 8.4302 14.2269 9.03924 14.2269 9.7849C14.2269 10.5306 14.846 11.1396 15.6038 11.1396H21.1231C21.8868 11.1396 22.5 10.5306 22.5 9.7849V4.3604C22.5 3.60905 21.881 3 21.1231 3C20.3594 3 19.7404 3.60905 19.7404 4.3604V6.52906L18.9825 5.7834C15.2104 2.0722 9.1011 2.0722 5.32904 5.7834C1.55699 9.49461 1.55699 15.5054 5.32904 19.2166C9.1011 22.9278 15.2104 22.9278 18.9825 19.2166C19.5205 18.6872 19.5205 17.8277 18.9825 17.2984C18.4445 16.769 17.5709 16.769 17.0328 17.2984C14.3369 19.9509 9.97469 19.9509 7.27871 17.2984C4.58273 14.6459 4.58273 10.3541 7.27871 7.70162C9.97469 5.04913 14.3369 5.04913 17.0328 7.70162L17.7734 8.4302Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.financing === "TBC" &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.689 3.5C15.4991 3.56337 18.1922 4.43158 20.3553 6.68131C23.3195 9.7739 23.1962 14.3874 20.0779 17.3279C18.2846 19.02 16.1462 19.8692 13.7489 20.148C12.0357 20.3445 10.3842 20.1227 8.74492 19.6284C8.56004 19.5713 8.28272 19.622 8.1225 19.7298C6.78521 20.617 5.36165 21.257 3.7717 21.4662C3.70391 21.4725 3.63612 21.4915 3.5745 21.4852C3.22323 21.4598 2.81034 21.6373 2.58232 21.2C2.34198 20.7374 2.68092 20.4585 2.92126 20.1607C3.61764 19.2988 4.10448 18.3229 4.29552 17.2202C4.33866 16.9857 4.27087 16.6625 4.13529 16.4787C1.55933 13.101 2.07699 8.69023 5.36781 5.97154C7.45077 4.25414 9.86035 3.53169 12.689 3.5ZM12.3131 6.67497C12.3131 6.68131 12.3131 6.68131 12.3131 6.68765C11.8447 6.68765 11.3763 6.6623 10.908 6.69399C10.0329 6.75102 9.33036 7.35306 9.09619 8.22127C8.93596 8.82331 9.127 9.2796 9.63233 9.49506C10.1007 9.69152 10.5506 9.49506 10.8772 9.01977C10.9881 8.86133 11.2223 8.70924 11.4072 8.7029C12.0604 8.66488 12.7136 8.65854 13.3607 8.7029C13.5764 8.72191 13.9277 8.91837 13.9462 9.0768C13.977 9.29227 13.8167 9.62815 13.638 9.7739C13.2621 10.0781 12.8307 10.3379 12.3932 10.5344C11.5982 10.8893 11.4441 11.523 11.5427 12.3025C11.5982 12.7397 11.8324 13.0376 12.2514 13.1327C12.7013 13.2341 13.145 13.1327 13.3114 12.6764C13.4778 12.2201 13.7921 12.011 14.1618 11.8082C14.4145 11.6687 14.661 11.523 14.8952 11.3582C15.8319 10.6928 16.1893 9.59646 15.8627 8.44941C15.5792 7.4608 14.6302 6.72567 13.5641 6.68765C13.1512 6.6623 12.7321 6.67497 12.3131 6.67497ZM11.2346 15.7119C11.2346 16.5168 11.7399 17.0618 12.4918 17.0681C13.2313 17.0744 13.7736 16.5041 13.7736 15.7246C13.7736 14.9324 13.2991 14.4318 12.5349 14.4191C11.7153 14.4128 11.2346 14.8881 11.2346 15.7119Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      {
                        data?.lead?.financing === null &&
                        <Stack sx={{ color: "text.secondary" }}>
                          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.689 3.5C15.4991 3.56337 18.1922 4.43158 20.3553 6.68131C23.3195 9.7739 23.1962 14.3874 20.0779 17.3279C18.2846 19.02 16.1462 19.8692 13.7489 20.148C12.0357 20.3445 10.3842 20.1227 8.74492 19.6284C8.56004 19.5713 8.28272 19.622 8.1225 19.7298C6.78521 20.617 5.36165 21.257 3.7717 21.4662C3.70391 21.4725 3.63612 21.4915 3.5745 21.4852C3.22323 21.4598 2.81034 21.6373 2.58232 21.2C2.34198 20.7374 2.68092 20.4585 2.92126 20.1607C3.61764 19.2988 4.10448 18.3229 4.29552 17.2202C4.33866 16.9857 4.27087 16.6625 4.13529 16.4787C1.55933 13.101 2.07699 8.69023 5.36781 5.97154C7.45077 4.25414 9.86035 3.53169 12.689 3.5ZM12.3131 6.67497C12.3131 6.68131 12.3131 6.68131 12.3131 6.68765C11.8447 6.68765 11.3763 6.6623 10.908 6.69399C10.0329 6.75102 9.33036 7.35306 9.09619 8.22127C8.93596 8.82331 9.127 9.2796 9.63233 9.49506C10.1007 9.69152 10.5506 9.49506 10.8772 9.01977C10.9881 8.86133 11.2223 8.70924 11.4072 8.7029C12.0604 8.66488 12.7136 8.65854 13.3607 8.7029C13.5764 8.72191 13.9277 8.91837 13.9462 9.0768C13.977 9.29227 13.8167 9.62815 13.638 9.7739C13.2621 10.0781 12.8307 10.3379 12.3932 10.5344C11.5982 10.8893 11.4441 11.523 11.5427 12.3025C11.5982 12.7397 11.8324 13.0376 12.2514 13.1327C12.7013 13.2341 13.145 13.1327 13.3114 12.6764C13.4778 12.2201 13.7921 12.011 14.1618 11.8082C14.4145 11.6687 14.661 11.523 14.8952 11.3582C15.8319 10.6928 16.1893 9.59646 15.8627 8.44941C15.5792 7.4608 14.6302 6.72567 13.5641 6.68765C13.1512 6.6623 12.7321 6.67497 12.3131 6.67497ZM11.2346 15.7119C11.2346 16.5168 11.7399 17.0618 12.4918 17.0681C13.2313 17.0744 13.7736 16.5041 13.7736 15.7246C13.7736 14.9324 13.2991 14.4318 12.5349 14.4191C11.7153 14.4128 11.2346 14.8881 11.2346 15.7119Z" fill="currentcolor" />
                          </svg>
                        </Stack>
                      }
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        marginLeft="10px"
                      >
                        {data?.lead?.financingTypeDisplay || 'TBC'}
                      </Typography>
                    </ListItem>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    padding="4px 0"
                    minHeight="40px"
                    style={{ wordBreak: "break-word" }}
                    className="multiLineTruncate"
                  >
                    {data.lead.message
                      ? showNote(data.lead.message, WORDS_110)
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
        {(caseStatusDetail?.[0]?.status.value ||
          caseProducts.results?.length > 0) && (
            <Grid item xs={12}>
              <Box className={classes.box}>
                {caseStatusDetail?.[0]?.status.value && (
                  <>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      component="div"
                    >
                      Status
                    </Typography>
                    <Stack direction="row" alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body1"
                        color="text.primary"
                        component="div"
                        style={{ marginRight: "24px" }}
                      >
                        {caseStatusDetail?.[0]?.status.value}
                      </Typography>
                      <Chip
                        label={moment(caseStatusDetail?.[0]?.created).fromNow()}
                        sx={theme => CHIP_STYLE(theme)}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      {moment(caseStatusDetail[0].created).format(
                        "ddd D MMM YYYY hh:mm A"
                      )}
                    </Typography>
                  </>
                )}
                {caseProducts.results?.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle1"
                      color="text.primary"
                      component="div"
                      gutterBottom
                      sx={theme => ({
                        fontFamily: "'Nunito Sans', sans-serif",
                        [theme.breakpoints.down("lg")]: {
                          marginTop: "24px"
                        }
                      })}
                    >
                      Products
                    </Typography>
                    <Stack direction="row" flexWrap="wrap">
                      {caseProducts?.results?.map(caseProduct => (
                        <RouterLink
                          to={`/cases/${id}/edit-product/${caseProduct.id}`}
                          key={caseProduct.id}
                          style={{ textDecoration: "none" }}
                        >
                          <Chip
                            label={`${caseProduct?.type}: ${caseProduct.product?.name}`}
                            sx={theme => CHIP_STYLE(theme)}
                          />
                        </RouterLink>
                      ))}
                    </Stack>
                  </>
                )}
              </Box>
            </Grid>
          )}
        <Box sx={{ marginTop: "28px", marginBottom: "24px" }}>
          <CaseDetailTabs />
        </Box>
      </div>
      {isModal && (
        <ClientContactModal
          isOpen={isModal}
          handleClose={() => setIsModal(false)}
          clientDetail={clientDetail}
        />
      )}
    </>
  );
}

export default Case;
